import { UrlBuilder } from "@lib-shared/lib";
import BaseCefCommercialService from "./base.service";
import { CefVersion } from "./constants";
import { FinancialAccount } from "./customer-account.service.types";
import { AmountWithCurrency, PrimaryContactDetails } from "./types";
import { activityLogArea, UserActivityLogger } from "../user-tracking-helper";

export type CompanyDetails = {
    businessRegistrationNumber: string,
    companyName: string,
    vatNumber: string
}

export type PrimaryPersonalDetails = {
    firstName: string,
    role: string,
    initials: string,
    surname: string,
    position: string,
    title: string
}

export type PrimaryEmailAddress = {
    email: string
}

export type PrimaryAddressDetails = {
    country: string,
    city: string,
    addressType: string,
    postalCode: string,
    houseNumber: string,
    buildingName: string,
    suite: string,
    province: string,
    street: string,
    district: string,
    suburb: string,
    buildingNumber: string,
    postBoxNumber: string,
    floor: string
}

export type CustomerAccountResponse = {
    companyDetails: CompanyDetails,
    primaryPersonalDetails: PrimaryPersonalDetails[],
    primaryContactDetails: PrimaryContactDetails[],
    primaryAddressDetails: PrimaryAddressDetails[],
    primaryEmailAddress: PrimaryEmailAddress[]
}

export type PackageInfoData = {
    packageType: string,
    totalTV: string
}

export type PackageInfo = {
    totalTV: string,
    packageType: string,
    monthlyFee: string,
    status: string
    data: PackageInfoData[]
}

export type CustomerProduct = {
    alternativeNames: string[], //still used?
    uid: string, //still used?
    financialAccountNumber: number,
    establishmentName: string, //still used?
    invoiceDay: number,
    agreements: CustomerProductsForAgreement[],
    packageInfo: PackageInfo[]
}

export type Hardware = {
    smartcardNumber: string,
    services: Service[]
}

export type Service = {
    packageDetail: PackageDetail,
    status: string
}
export type PackageDetail = {
    price: AmountWithCurrency,
    agreementId: string,
    packageName: string,
    packageType: string
}

export type CustomerProductsForAgreement = {
    numberOfViewingPoints: string,
    hardwares: Hardware[],
    agreementId: string,
    establishmentName: string,
    locationIndicator: string,
    agreementStatus: string
}

export type ChangePackageInfo = {
    name: string,
    packageId: string
}

export type ChangePackageResponse = {
    packageId: string,
    from: string,
    upgradeTo: ChangePackageInfo[],
    downgradeTo: ChangePackageInfo[]
}

export type ChangePackagePricingResponse = {
    agreementId: string,
    priceDetails: PriceDetails
}

export type PriceDetails = {
    lineItems: LineItem[],
    totalAmount: number
}

export type LineItem = {
    amount: AmountWithCurrency,
    invoiceText: string,
    commercialProductId: number,
    endDate: string,
    isProRated: boolean,
    startDate: string | Date
}

class CustomerAccountService extends BaseCefCommercialService {
    constructor() {
        super(activityLogArea.customerAccount);
        this.setBaseUrl(`CEFCommCustomerAccountAPI/${CefVersion}/customers/`);
        this.validateCustomerNumber();
    }

    getCustomerAccount(customerNumber: string | null = null) {
        this.userTracking.read();

        const cn = this.customerNumber;

        return this.createPromiseRequest(`${customerNumber ?? cn}`).get<CustomerAccountResponse>();
    }

    getSubscriptions() {
        new UserActivityLogger(activityLogArea.products).read();

        return this.createPromiseRequest(`${this.customerNumber}/products`).get<CustomerProduct[]>();
    }

    getAgreements() {
        new UserActivityLogger(activityLogArea.products).log("Agreements");

        return this.createPromiseRequest(`${this.customerNumber}/agreements`).get<FinancialAccount[]>();
    }

    changePackage(fromPackageName: string) {
        const path = new UrlBuilder(this.customerNumber.toString())
            .appendPath("changePackage")
            .appendParam("fromPackageName", fromPackageName)
            .toString();

        return this.createPromiseRequest(path).get<ChangePackageResponse>();
    }

    changePackagePrice(agreementId: number, fromPackageName: string, toPackageName: string) {
        const path = new UrlBuilder(this.customerNumber.toString())
            .appendPath("changePackagePricing")
            .appendParam("fromPackageName", fromPackageName)
            .appendParam("toPackageName", toPackageName)
            .appendParam("agreementId", agreementId)
            .toString();

        return this.createPromiseRequest(path).get<ChangePackagePricingResponse>();
    }

    async getAllChangePackagePricesAsync(agreementId: number, response: ChangePackageResponse) {
        const prices: PriceDetails[] = [];

        const getPrices = async (packages: ChangePackageInfo[]) => {
            if (!(packages?.length ?? false)) {
                return;
            }

            for (let i = 0; i < packages.length; i++) {
                const priceResponse = await this.changePackagePrice(agreementId, response.from, packages[i].name);

                prices.push(priceResponse.data.priceDetails);
            }
        }

        await getPrices(response?.downgradeTo);
        await getPrices(response?.upgradeTo);

        return prices;
    }
}

export default CustomerAccountService;
